import type { Pipeline } from '@/lib/crm/hooks/usePipelines';
import { formatPipelineLabel } from '@/lib/crm/pipeline/utils/formatPipelineLabel';

import type { NavListItem } from './NavList';
import { getPipelineColor } from './pipeline-colors';

export interface BuildPipelineNavItemArgs {
  orgId: string;
  entitySlug: string;
  label: string;
  icon: NavListItem['icon'];
  pipelines: Pipeline[];
  onPrefetch?: NavListItem['onPrefetch'];
  // shortcut is intentionally optional: custom entities with pipelines have no keyboard
  // shortcut registered in v1 (scope decision). Deals may pass this in future.
  shortcut?: string;
}

export function buildPipelineNavItem(
  args: BuildPipelineNavItemArgs
): NavListItem {
  const { orgId, entitySlug, label, icon, pipelines, onPrefetch, shortcut } =
    args;

  const sortedPipelines = [...pipelines].sort((a, b) => {
    if (a.is_default && !b.is_default) {
      return -1;
    }
    if (!a.is_default && b.is_default) {
      return 1;
    }
    const aLabel = a.label || formatPipelineLabel(a.pipeline_name);
    const bLabel = b.label || formatPipelineLabel(b.pipeline_name);
    return aLabel.localeCompare(bLabel);
  });

  const buildPipelineHref = (p: Pipeline): string =>
    p.entity_id
      ? `/${orgId}/pipeline/${entitySlug}/${p.entity_id}`
      : `/${orgId}/crm/${entitySlug}?pipeline=${encodeURIComponent(p.pipeline_name)}`;

  // onPrefetch warms the CRM list query, which only matches the CRM list
  // view. A link straight to a pipeline page (entity_id present) would
  // prefetch the wrong data and waste the request — drop it there. It stays
  // relevant for the pipeline-filtered list fallback (no entity_id), whose
  // link targets the list view.
  const prefetchFor = (p: Pipeline): NavListItem['onPrefetch'] =>
    p.entity_id ? undefined : onPrefetch;

  // Exactly one pipeline: skip the child level entirely — no collapse
  // chevron, and the nav item links straight to that pipeline.
  if (sortedPipelines.length === 1) {
    return {
      label,
      href: buildPipelineHref(sortedPipelines[0]),
      icon,
      onPrefetch: prefetchFor(sortedPipelines[0]),
      shortcut,
    };
  }

  const children: NavListItem[] | undefined =
    sortedPipelines.length === 0
      ? undefined
      : sortedPipelines.map((p, index) => ({
          label: p.label || formatPipelineLabel(p.pipeline_name),
          href: buildPipelineHref(p),
          icon,
          pipelineColorClass: getPipelineColor(index),
          onPrefetch: prefetchFor(p),
        }));

  return {
    label,
    href: `/${orgId}/crm/${entitySlug}?view=table`,
    icon,
    onPrefetch,
    shortcut,
    children,
  };
}
